import {
  Select,
  Table,
  Input,
  Space,
  Modal,
  Form,
  InputNumber,
  notification,
  Card,
  Tag,
  Radio,
  Button,
  Popover,
} from "antd";
import {
  ExclamationCircleOutlined,
  CheckCircleFilled,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { useContext, useEffect, useState } from "react";
import * as tus from "tus-js-client";
import moment from "moment";

import { Context } from "../../context";
import { Fetch, Post, Put, Delete } from "../../fetch";
import { convertIdToKey, countryCodes, handleExportClick } from "../../utils";
import ExportButton from "../ExportButton";
import UploadBanners from "../UploadBanners/index";

const { Option } = Select;
const { Column } = Table;
const { confirm } = Modal;

const ManageSongs = () => {
  const { apiUrl, token, transcodeUrl, superAdmin } = useContext(Context);

  const [songs, setSongs] = useState([]);
  const [songsMaster, setSongsMaster] = useState([]);
  const [song, setSong] = useState();
  const [actors, setActors] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [contentExists, setContentExists] = useState(false);
  const [counter, setCounter] = useState(0);
  const [directors, setDirectors] = useState([]);
  const [producers, setProducers] = useState([]);
  const [singers, setSingers] = useState([]);
  const [lyricists, setLyricists] = useState([]);
  const [genres, setGenres] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [listType, setListType] = useState("allow_all");
  const [allowList, setAllowList] = useState([]);

  const [form] = Form.useForm();

  useEffect(() => {
    Fetch(`${apiUrl}/songs`, token, (result) => {
      const songs = convertIdToKey(result.songs);
      songs.forEach((s) => {
        if (!s.videoUploadDateTime) {
          s.videoUploadDateTime = "NA";
        } else {
          s.videoUploadDateTime = moment(s.videoUploadDateTime).format(
            "DD/MM/YYYY hh:mm A"
          );
        }
      });
      const unpublished = sessionStorage.getItem("unpublished");
      if (unpublished) {
        const result = songs.filter((s) => s.availability === "unpublished");
        setSongs(result);
        setSongsMaster(result);
      } else {
        setSongs(songs);
        setSongsMaster(songs);
      }
    });

    Fetch(`${apiUrl}/artists`, token, (result) => {
      setActors(result.artists);
    });

    Fetch(`${apiUrl}/genres`, token, (result) => {
      setGenres(result.genres);
    });

    Fetch(`${apiUrl}/languages`, token, (result) => {
      setLanguages(result.languages);
    });

    Fetch(`${apiUrl}/mediaHouses`, token, (result) => {
      setDirectors(result.directors);
      setProducers(result.producers);
      setSingers(result.singers);
      setLyricists(result.lyricists);
    });
  }, [apiUrl, token, counter]);

  useEffect(() => {
    if (!song) {
      return;
    }

    let values = song.allowedCountries;
    let countries = [];
    if (listType === "allow") {
      form.setFieldValue("allowedCountries", values);
    }
    if (listType === "disallow") {
      for (const i in countryCodes) {
        if (!values.includes(i)) {
          countries.push(i);
        }
      }
      form.setFieldValue("allowedCountries", countries);
      countries = [];
    }
  }, [listType, form.getFieldValue("countryMap")]);

  const showEditModal = (record) => {
    setSong(record);
    setContentExists(record.playbackUrl ? true : false);
    setModalVisible(true);
    form.setFieldsValue(record);
    let values = record.allowedCountries;
    if (record.listType) {
      form.setFieldValue("listType", record.listType);
      setListType(record.listType);
    } else {
      setListType("allow_all");
      form.setFieldValue("listType", "allow_all");
    }
    let countries = [];
    if (record.listType === "allow") {
      form.setFieldValue("countryMap", values);
    }
    if (record.listType === "disallow") {
      for (const i in countryCodes) {
        if (!values.includes(i)) {
          countries.push(i);
        }
      }
      form.setFieldValue("countryMap", countries);
      countries = [];
    }
  };

  const handleSubmit = async () => {
    setConfirmLoading(true);
    try {
      const values = await form.validateFields();
      if (listType === "allow") {
        for (const i in countryCodes) {
          if (values.countryMap.includes(i)) allowList.push(i);
        }
      }
      if (listType === "disallow") {
        for (const i in countryCodes) {
          if (!values.countryMap.includes(i)) allowList.push(i);
        }
      }
      if (listType === "allow_all") {
        for (const i in countryCodes) {
          allowList.push(i);
        }
      }
      Put(
        `${apiUrl}/songs/${song.key}`,
        token,
        {
          ...values,
          allowedCountries: allowList,
          listType: listType,
        },
        (result) => {
          if (result.success) {
            form.resetFields();
            notification.success({
              message: "Success",
              description: "Song edited",
            });
            setModalVisible(false);
            setCounter(counter + 1);
            setAllowList([]);
          } else {
            const playlists = result.detail.playlists;
            const promos = result.detail.promos;

            if (playlists.length > 0 && promos.length > 0) {
              notification.error({
                message: "Error updating song",
                description: `Please delete from playlists [${playlists.join(
                  ", "
                )}] and promos [${promos.join(", ")}] first`,
              });
            } else if (playlists.length > 0) {
              notification.error({
                message: "Error updating song",
                description: `Please delete from playlists [${playlists.join(
                  ", "
                )}] first`,
              });
            } else if (promos.length > 0) {
              notification.error({
                message: "Error updating song",
                description: `Please delete from promos [${promos.join(
                  ", "
                )}] first`,
              });
            }
          }
        },
        () => {
          notification.error({
            message: "Error",
            description: "Please check console for more details",
          });
        }
      );
    } catch (err) {
      console.error(err);
      notification.error({
        message: "Error",
        description: "Required fields incorrect/missing",
      });
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleUploadChange = (e) => {
    const file = e.target.files[0];

    setUploading(true);

    const upload = new tus.Upload(file, {
      endpoint: `${transcodeUrl}/files`,
      retryDelays: [0, 3000, 5000, 10000, 20000],
      metadata: {
        filename: file.name,
        filetype: file.type,
      },
      onError: (error) => {
        console.log("Failed because: " + error);
      },
      onProgress: (bytesUploaded, bytesTotal) => {
        var percentage = ((bytesUploaded / bytesTotal) * 100).toFixed(2);
        setUploadProgress(percentage);
        console.log(bytesUploaded, bytesTotal, percentage + "%");
      },
      onSuccess: () => {
        Post(
          `${transcodeUrl}/submitJob`,
          token,
          {
            id: song.key,
            loc: upload.url.split("/").slice(-1)[0],
            media_type: "songs",
          },
          (result) => {
            console.log(result);
          },
          () => {
            console.error(`Error transcoding content`);
          }
        );
        console.log("Download %s from %s", upload.file.name, upload.url);
      },
    });

    upload.start();
  };

  const confirmDelete = (record) => {
    confirm({
      title: "Are you sure you want to delete this song?",
      icon: <ExclamationCircleOutlined />,
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk() {
        Delete(
          `${apiUrl}/songs/${record.key}`,
          token,
          (result) => {
            if (result.success) {
              notification.success({
                message: "Succes",
                description: "Song deleted successfully",
              });
              setCounter(counter + 1);
            } else {
              const playlists = result.detail.playlists;
              const promos = result.detail.promos;

              if (playlists.length > 0 && promos.length > 0) {
                notification.error({
                  message: "Error deleting song",
                  description: `Please delete from playlists [${playlists.join(
                    ", "
                  )}] and promos [${promos.join(", ")}] first`,
                });
              } else if (playlists.length > 0) {
                notification.error({
                  message: "Error deleting song",
                  description: `Please delete from playlists [${playlists.join(
                    ", "
                  )}] first`,
                });
              } else if (promos.length > 0) {
                notification.error({
                  message: "Error deleting song",
                  description: `Please delete from promos [${promos.join(
                    ", "
                  )}] first`,
                });
              }
            }
          },
          () => {
            notification.error({
              message: "Error deleting song",
              description: "Please check console for more details",
            });
          }
        );
      },
    });
  };

  const handleSearchChange = (songId) => {
    if (songId === undefined) return;
    Fetch(`${apiUrl}/songs/${songId}`, token, (result) => {
      const song = convertIdToKey([result]);
      song.forEach((song) => {
        if (!song.videoUploadDateTime) {
          song.videoUploadDateTime = "NA";
        } else {
          song.videoUploadDateTime = moment(song.videoUploadDateTime).format(
            "DD/MM/YYYY hh:mm A"
          );
        }
      });
      setSongs(song);
    });
  };

  return (
    <div className="manage-episodes-wrapper">
      <Select
        placeholder="Search Song"
        showSearch
        allowClear
        onChange={handleSearchChange}
        onClear={() => setSongs(songsMaster)}
        optionFilterProp="children"
        filterOption={(input, option) =>
          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
        filterSort={(optionA, optionB) =>
          optionA.children
            .toLowerCase()
            .localeCompare(optionB.children.toLowerCase())
        }
      >
        {songsMaster.map((m) => (
          <Option value={m.key}>{m.title}</Option>
        ))}
      </Select>
      <Table dataSource={songs}>
        <Column title="Title" dataIndex="title" key="name" align="center" />
        <Column
          title="Language"
          dataIndex="language"
          key="language"
          align="center"
        />
        <Column
          title="Genres"
          dataIndex="genre"
          key="genre"
          align="center"
          render={(values) => (
            <>
              {values.map((v) => (
                <Tag color="blue">{v}</Tag>
              ))}
            </>
          )}
        />
        <Column title="Model" dataIndex="model" key="model" align="center" />
        <Column
          title="Availability"
          dataIndex="availability"
          key="availability"
          align="center"
        />
        <Column
          title="Content Exists"
          key="playbackStatusFp"
          align="center"
          render={(_, record) => {
            const { drm, private: privateStatus } = record.playbackStatusFp || {};
            const isReady = drm === "ready" && privateStatus === "ready";
            return isReady ? <CheckCircleFilled /> : <CloseCircleOutlined />;
          }}
        />
        <Column
          title="Video Uploaded Date"
          dataIndex="videoUploadDateTime"
          key="videoUploadDateTime"
          align="center"
        />
        <Column
          title="Actions"
          key="action"
          align="center"
          render={(text, record) => {
            if (superAdmin || record.availability === "unpublished") {
              if (record.comments) {
                return (
                  <Space size="middle">
                    <Popover
                      content={record.comments}
                      title="Admin Comments"
                      trigger="click"
                    >
                      <Button>View Comments</Button>
                    </Popover>
                    <Button onClick={() => showEditModal(record)}>Edit</Button>
                    <Button
                      onClick={() => {
                        confirmDelete(record);
                      }}
                    >
                      Delete
                    </Button>
                  </Space>
                );
              }
              return (
                <Space size="middle">
                  <Button onClick={() => showEditModal(record)}>Edit</Button>
                  <Button
                    onClick={() => {
                      confirmDelete(record);
                    }}
                  >
                    Delete
                  </Button>
                </Space>
              );
            } else {
              return "-";
            }
          }}
        />
      </Table>
      <ExportButton
        className="export-btn-only"
        onExport={() => handleExportClick(apiUrl, token, "songs")}
      />
      <Modal
        title="Edit Song"
        visible={modalVisible}
        onOk={handleSubmit}
        okText="Update"
        onCancel={() => setModalVisible(false)}
        confirmLoading={confirmLoading}
        maskClosable={false}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input size="middle" />
          </Form.Item>
          <Form.Item
            name="language"
            label="Language"
            rules={[{ required: true }]}
          >
            <Select placeholder="Choose a language" showSearch>
              {languages.map((language) => (
                <Option value={language.name}>{language.name}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="genre"
            label="Genre / Category"
            rules={[{ required: true }]}
          >
            <Select placeholder="Choose a genre" mode="multiple" showSearch>
              {genres.map((genre) => (
                <Option value={genre.name}>{genre.name}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="maturity"
            label="Maturity"
            rules={[{ required: true }]}
          >
            <Select placeholder="Choose maturity">
              <Option value="U">U (Universal)</Option>
              <Option value="U/A 7+">U/A 7+ (For Ages 7 and Above)</Option>
              <Option value="U/A 13+">U/A 13+ (For Ages 13 and Above)</Option>
              <Option value="U/A 16+">U/A 16+ (For Ages 16 and Above)</Option>
              <Option value="A">A (Adults - Ages 18 and Above)</Option>
            </Select>
          </Form.Item>
          <Form.Item name="year" label="Year" rules={[{ required: true }]}>
            <InputNumber min={1} />
          </Form.Item>
          <Card title="Cast &amp; Crew" className="form-card">
            <Form.Item
              name="actors"
              label="Actors"
              rules={[{ required: true }]}
            >
              <Select showSearch mode="multiple" className="form-card-selector">
                {actors.map((actor) => (
                  <Option key={actor.key} value={actor.name}>
                    {actor.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="singers"
              label="Singers"
              rules={[{ required: true }]}
            >
              <Select showSearch mode="multiple">
                {singers.map((singer) => (
                  <Option key={singer.key} value={singer.name}>
                    {singer.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="lyricists"
              label="Lyricists"
              rules={[{ required: true }]}
            >
              <Select showSearch mode="multiple">
                {lyricists.map((lyricist) => (
                  <Option key={lyricist.key} value={lyricist.name}>
                    {lyricist.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="directors" label="Directors">
              <Select showSearch mode="multiple" className="form-card-selector">
                {directors.map((director) => (
                  <Option key={director.key} value={director.name}>
                    {director.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="producers" label="Producers">
              <Select showSearch mode="multiple">
                {producers.map((producer) => (
                  <Option key={producer.key} value={producer.name}>
                    {producer.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Card>
          <Form.Item
            name="cardImage"
            label="Card Image"
            rules={[{ required: true }]}
          >
            <UploadBanners
              name={"cardImageSong3_4"}
              type="song"
              updateForm={form}
            />
          </Form.Item>
          <Form.Item
            name="trailerImage"
            label="Wide Image (for continue watch in web)"
            rules={[{ required: true }]}
          >
            <UploadBanners
              name={"trailerImage"}
              type="song"
              updateForm={form}
            />
          </Form.Item>
          <Form.Item
            name="listType"
            label={"Content access"}
            rules={[{ required: true }]}
            initialValue={listType}
          >
            <Radio.Group
              className="content-type-option"
              value={listType}
              onChange={(e) => setListType(e.target.value)}
              defaultValue={"allow_all"}
            >
              <Radio value="allow_all">Allow Globally</Radio>
              <Radio value="allow">Allow List</Radio>
              <Radio value="disallow">Deny List</Radio>
            </Radio.Group>
          </Form.Item>

          {["allow", "disallow"].includes(listType) && (
            <Form.Item
              name={`countryMap`}
              label={"Countries"}
              rules={[{ required: true }]}
              style={{ width: "100%" }}
            >
              <Select
                showSearch
                mode="multiple"
                className="form-card-selector"
                style={{ width: "100%" }}
              >
                {Object.entries(countryCodes).map(([code, name]) => (
                  <Option key={code} value={code}>
                    {name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}
          <Form.Item
            name="duration"
            label="Duration"
            className="form-item-selector"
            rules={[{ required: true }]}
          >
            <InputNumber placeholder="Enter duration in seconds" />
          </Form.Item>
          <Form.Item label="Model" name="model">
            <Radio.Group>
              <Radio value="subscription">Subscription</Radio>
              <Radio value="free">Free</Radio>
            </Radio.Group>
          </Form.Item>
          {superAdmin && (
            <Form.Item
              name="availability"
              label="Availability"
              rules={[{ required: true }]}
            >
              <Radio.Group>
                <Radio value="perpetual">Perpetual</Radio>
                <Radio value="unpublished">Unpublished</Radio>
              </Radio.Group>
            </Form.Item>
          )}
          {superAdmin && (
            <Form.Item name="comments" label="Comments">
              <Input />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default ManageSongs;
